import { Carousel } from '@design-system';
import { shopifyGraphqlIdToAdminId } from '@utils/shopifyIds';
import React, { useMemo } from 'react';

import { useProducts } from '../../../hooks/useProducts';
import { useGetDrawByProductIDsQuery } from '../../../types/generated';
import { TrackCarouselSlide } from '../../../utils/analytics';
import ProductTile from '../../ProductTile';

type Props = {
  productIds: Array<string>;
  heading?: string | null;
  carouselPerPage?: 3 | 4;
  analytics?: Analytics & { carouselTitle?: string };
};

const ProductsByIdCarousel: React.FC<Props> = ({
  analytics,
  carouselPerPage = 4,
  heading,
  productIds,
}) => {
  const { products } = useProducts(productIds);
  const productIDs = products.map((p) => shopifyGraphqlIdToAdminId(p.id));
  const { data: draws } = useGetDrawByProductIDsQuery({
    variables: { productIDs },
    skip: productIDs.length === 0,
  });
  const productTileComponents = useMemo(
    () =>
      products.map((product, idx) => {
        const draw = draws?.getDrawByProductIDs?.find((d) => {
          return d.productId === shopifyGraphqlIdToAdminId(product.id);
        });

        return (
          <ProductTile
            key={product.id}
            analytics={
              analytics && {
                page: analytics.page,
                context: analytics.context,
                position: idx,
              }
            }
            draw={draw}
            product={product}
          />
        );
      }),
    [products],
  );

  if (!products || !products?.length) return null;

  return (
    <Carousel
      headline={heading}
      items={productTileComponents}
      onSlide={(index: number) => {
        analytics &&
          TrackCarouselSlide({
            page: analytics.page,
            context: analytics.context,
            title: analytics.carouselTitle || products[index].title,
            position: index,
            carouselLength: products.length,
          });
      }}
      perPage={carouselPerPage}
    />
  );
};

export default ProductsByIdCarousel;
